import { maxLookbackDays } from "modules/common/components/Alarms/Form/components/Measurements/schemas/ThresholdInputSchema";
import { AlarmTypeEnum } from "services";
import z from "zod";

const stationAlarmSchema = z
    .object({
        id: z.number().int().optional(),
        metosStationId: z.number().int().optional(),
        pinovaStationId: z.number().int().optional(),

        measurementType: z.string().nonempty(),
        measurementUnit: z.string().optional().nullable(),

        type: z.nativeEnum(AlarmTypeEnum),
    })
    .refine(d => d.metosStationId != null || d.pinovaStationId != null, {
        message: "Mora biti odabrana barem jedna vrsta stanice (Metos ili Pinova)",
    })
    .and(
        z.discriminatedUnion("type", [
            z.object({
                type: z.literal(AlarmTypeEnum.Regular),
                valueRange: z.object({
                    min: z.number({
                        required_error: "Donja granica je obavezna",
                        invalid_type_error: "Donja granica mora biti broj",
                    }),
                    max: z.number({
                        required_error: "Gornja granica je obavezna",
                        invalid_type_error: "Gornja granica mora biti broj",
                    }),
                }),
                amount: z.undefined().nullable().optional(),
                lookbackDays: z.undefined().nullable().optional(),
                phenophaseId: z.undefined().nullable().optional(),
            }),
            z.object({
                type: z.literal(AlarmTypeEnum.ForSpecificLoockbackDays),
                amount: z
                    .number({
                        required_error: "Količina je obavezna",
                        invalid_type_error: "Količina mora biti broj",
                    })
                    .min(0, "Količina mora biti ≥ 0"),
                lookbackDays: z
                    .number({
                        required_error: "Broj dana je obavezan",
                        invalid_type_error: "Broj dana mora biti broj",
                    })
                    .int()
                    .min(1, "Broj dana mora biti ≥ 1")
                    .max(maxLookbackDays, `Broj dana mora biti ≤ ${maxLookbackDays}`),
                valueRange: z.undefined().nullable().optional(),
                phenophaseId: z.undefined().nullable().optional(),
            }),
            z.object({
                type: z.literal(AlarmTypeEnum.Phenophase),
                phenophaseId: z
                    .number({
                        required_error: "Fenofaza je obavezna",
                        invalid_type_error: "Fenofaza mora biti broj",
                    })
                    .int()
                    .min(1, "Fenofaza mora biti odabrana"),
                amount: z.undefined().nullable().optional(),
                lookbackDays: z.undefined().nullable().optional(),
                valueRange: z.undefined().nullable().optional(),
            }),
        ])
    )
    .refine(
        data => {
            if (data.type === AlarmTypeEnum.Regular && data.valueRange) {
                const { min, max } = data.valueRange;
                if (!min && !max) {
                    return false;
                }

                if (min >= max) {
                    return false;
                }

                if (data.measurementType !== "Temperatura zraka" && min < 0) {
                    return false;
                }
            }

            return true;
        },
        {
            message: "Donja i gornja granica su obavezne i donja mora biti manja od gornje",
            path: ["valueRange"],
        }
    )
    .refine(
        data => {
            if (
                data.type === AlarmTypeEnum.ForSpecificLoockbackDays &&
                (data.amount == null || data.lookbackDays == null)
            )
                return false;

            return true;
        },
        {
            message: "Količina i broj dana su obavezni",
        }
    )
    .refine(data => data.type !== AlarmTypeEnum.Phenophase || data.phenophaseId != null, {
        message: "Fenofaza je obavezna",
    });

export const payloadSchema = z.object({
    groupId: z.number().int().optional(),
    groupName: z.string().nonempty("Naziv grupe alarma je obavezan"),
    alarms: z
        .array(stationAlarmSchema)
        .min(1, "Mora biti barem jedan alarm")
        .max(25, "Maksimalno 25 alarma po grupi"),
});

export type SaveStationAlarmsRequest = z.infer<typeof payloadSchema>;

export type CreateAlarmGroupRequest = Omit<SaveStationAlarmsRequest, "groupId">;
export type UpdateAlarmGroupRequest = Required<Pick<SaveStationAlarmsRequest, "groupId">> &
    Omit<SaveStationAlarmsRequest, "groupId">;

export type DeleteAlarmGroupRequest = {
    id: number;
};
